import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'
import { AppVersionProvider } from '../lib/app-version.js'
import { CLIENT_BUILD, versionState } from '../lib/release.js'

const CHECK_EVERY_MS = 60_000

interface ServerRelease {
  build: string | null
  release: string | null
}

const QUIET: ServerRelease = { build: null, release: null }

/**
 * Asks the live server which image it is, and registers the service worker
 * so a newer one can be noticed while a tab stays open. The bar is the only
 * way onto that image: the worker waits, and a plain reload keeps the old one.
 */
export function ReleaseProvider({ children }: { children: ReactNode }) {
  const registration = useRef<ServiceWorkerRegistration | undefined>(undefined)
  const [server, setServer] = useState<ServerRelease>(QUIET)
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    immediate: true,
    onRegisteredSW(_url, registered) {
      registration.current = registered
    },
  })

  useEffect(() => {
    if (!import.meta.env.PROD) return
    let cancel = false
    const load = () => {
      void fetch('/health')
        .then((response) => (response.ok ? response.json() : null))
        .then((body: { clientBuild?: unknown; release?: unknown } | null) => {
          if (cancel || !body) return
          setServer({
            build: typeof body.clientBuild === 'string' ? body.clientBuild : null,
            release: typeof body.release === 'string' ? body.release : null,
          })
        })
        .catch(() => {
          // A failed check leaves the last answer. The next minute tries again.
        })
    }
    const look = () => {
      load()
      void registration.current?.update()
    }
    look()
    const timer = window.setInterval(look, CHECK_EVERY_MS)
    const onVisible = () => {
      if (document.visibilityState === 'visible') look()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancel = true
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  const version = versionState({
    clientBuild: CLIENT_BUILD,
    serverBuild: server.build,
    release: server.release,
    updateReady: needRefresh,
  })

  return (
    <AppVersionProvider current={version.current} behind={version.behind}>
      {children}
      {version.ready ? (
        <UpdateBar
          message={version.ready}
          onReload={() => {
            void registration.current?.update()
            void updateServiceWorker(true)
          }}
        />
      ) : null}
    </AppVersionProvider>
  )
}

export function UpdateBar({ message, onReload }: { message: string; onReload: () => void }) {
  return (
    <div
      role="status"
      className="fixed inset-x-0 top-0 z-[80] flex items-center justify-center gap-3 bg-ink-900 px-4 py-2.5 pt-[max(0.625rem,var(--safe-top))] text-sm text-white"
    >
      <p>{message}.</p>
      <button
        type="button"
        onClick={onReload}
        className="rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-ink-900"
      >
        Reload
      </button>
    </div>
  )
}
